// Sankey/Transaction Analysis page — drilldown orchestrator.
//
// Three modes:
//
//   1. Full case feed (no URL params) — fetches every enriched transaction
//      via the paginated useSWRInfinite full-feed.
//
//   2. DRFA drilldown (`?drfa_category=<name>`) — resolves the DRFA category
//      to an exact transaction-ID list server-side (DRFA hierarchy ≠ Livisibly
//      hierarchy, so client-side name matching does not work), then fetches
//      those enriched rows via useTransactionDrilldown.
//
//   3. Stat drilldown (`?stat=<flagged|uncategorized>`) — same shape, IDs
//      come from /case_stats/drilldown. Source: dashboard action cards.
//
// All three modes hand the resulting transactions + matching mutate to the
// shared TransactionAnalyzer component. Both fetch paths produce the same
// useSWRInfinite Page[] cache shape, so a single mutate call handles edits
// from any mode without an adapter.
//
// URL params are read live (not captured on mount) so the in-page
// "Clear filter" button — which navigates to the param-less URL — actually
// exits drill-down mode. Browser back/forward also behave intuitively:
// landing on a URL without params shows the full feed, regardless of how
// the user got there.

import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import CurrentCaseRequired from "../components/ui/current-case-required.js";
import { perspectiveMap } from '../components/ui/report-sankey-analysis.js';
import CaseDatabaseCheck from "../components/ui/case-database-check.js";
import { useGetEnrichedTransactions, flattenInfiniteData } from '../api/transactions.js';
import {
  useGetDRFADrilldownTransactionIDs,
  useGetDRFABusinessDrilldownTransactionIDs,
  useGetDRFACrossLedgerDrilldownTransactionIDs,
} from '../api/drfa_income_expense.js';
import { useGetCaseStatsDrilldownTransactionIDs } from '../api/casestats.js';
import { useTransactionDrilldown } from "../hooks/useTransactionDrilldown.js";
import TransactionAnalyzer from "../components/transactions/TransactionAnalyzer.js";

// Human-readable labels for the drilldown banner. Keep in sync with
// STAT_DRILLDOWN_RESOLVERS in livisibly_case_stats/index.py.
const STAT_LABELS = {
  flagged: 'Flagged for Review',
  uncategorized: 'Misc Income/Expense (Uncategorized)',
};

export default function SankeyAnalysisPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read URL params live — see file header. Strings, no memoization needed.
  const drfaCategoryName = searchParams.get('drfa_category') || null;
  // A measured self-employment line on the DRFA (one per business account).
  const drfaBusinessAccountId = searchParams.get('drfa_business_account') || null;
  // The "Business activity in personal accounts" measured line.
  const drfaCrossLedger = searchParams.get('drfa_cross_ledger') || null;
  const statKey = searchParams.get('stat') || null;

  const isDRFADrillDown = Boolean(drfaCategoryName);
  const isDRFABusinessDrillDown = !isDRFADrillDown && Boolean(drfaBusinessAccountId);
  const isDRFACrossLedgerDrillDown = !isDRFADrillDown && !isDRFABusinessDrillDown && Boolean(drfaCrossLedger);
  const isStatDrillDown = !isDRFADrillDown && !isDRFABusinessDrillDown && !isDRFACrossLedgerDrillDown
    && Boolean(statKey);
  const isDrillDown = isDRFADrillDown || isDRFABusinessDrillDown || isDRFACrossLedgerDrillDown || isStatDrillDown;

  // Resolve drilldown key → transaction ID list. Each resolver hook
  // short-circuits when its key is absent so only one fetch fires.
  const { data: drfaDrilldownData } = useGetDRFADrilldownTransactionIDs(
    drfaCategoryName,
    isDRFADrillDown,
  );
  const { data: drfaBusinessDrilldownData } = useGetDRFABusinessDrilldownTransactionIDs(
    drfaBusinessAccountId,
    isDRFABusinessDrillDown,
  );
  const { data: drfaCrossLedgerDrilldownData } = useGetDRFACrossLedgerDrilldownTransactionIDs(
    drfaCrossLedger,
    isDRFACrossLedgerDrillDown,
  );
  const { data: statDrilldownData } = useGetCaseStatsDrilldownTransactionIDs(
    statKey,
    isStatDrillDown,
  );
  const drilldownTransactionIDs = isDRFADrillDown
    ? (drfaDrilldownData?.transaction_ids || null)
    : isDRFABusinessDrillDown
      ? (drfaBusinessDrilldownData?.transaction_ids || null)
      : isDRFACrossLedgerDrillDown
        ? (drfaCrossLedgerDrilldownData?.transaction_ids || null)
      : isStatDrillDown
        ? (statDrilldownData?.transaction_ids || null)
        : null;

  // Drilldown enriched fetch (paginated, client-slices the ID list).
  const {
    transactions: drilldownTransactions,
    mutate: mutateDrilldown,
    isLoading: isLoadingDrilldown,
    totalCount: drilldownTotalCount,
    loadedCount: drilldownLoadedCount,
  } = useTransactionDrilldown({
    transactionIDs: drilldownTransactionIDs,
    isActive: isDrillDown,
  });

  // Full-feed fetch (paginated). Disabled in drill-down mode so we don't
  // pull every case transaction just to discard them.
  // includeWithheld is the §4.8 read-side opt-in: the feed normally carries
  // only live flags, and the "Withheld flags" filter in TransactionAnalyzer
  // turns it on to add the flag assessment's SUPPRESSED rows (with the §4.6
  // Restore action on them). Drill-down mode has no such read, so the filter
  // is not offered there and this stays false.
  const [includeWithheld, setIncludeWithheld] = useState(false);
  const {
    data: fullFeedData,
    isLoading: isLoadingFullFeed,
    mutate: mutateFullFeed,
  } = useGetEnrichedTransactions(!isDrillDown, undefined, includeWithheld);

  const fullFeedTransactions = useMemo(
    () => flattenInfiniteData(fullFeedData),
    [fullFeedData],
  );

  // Pick the active source. mutate types are identical (both useSWRInfinite
  // Page[] mutate) so TransactionAnalyzer's edit hooks work either way.
  const transactions = isDrillDown ? drilldownTransactions : fullFeedTransactions;
  const mutate = isDrillDown ? mutateDrilldown : mutateFullFeed;
  const isLoading = isDrillDown ? isLoadingDrilldown : isLoadingFullFeed;

  // Sankey perspective lives in the URL (/<perspective> route param).
  const defaultPerspective = "CATEGORY";
  const { perspective = defaultPerspective } = useParams();
  const [sankeyPerspective, setSankeyPerspective] = useState(
    perspective in perspectiveMap ? perspective : defaultPerspective,
  );
  useEffect(() => {
    if (perspective in perspectiveMap) setSankeyPerspective(perspective);
  }, [perspective]);

  // Drilldown banner config. The orchestrator owns the label + the
  // "clear filter" navigation; the analyzer just renders.
  const drilldown = isDrillDown
    ? {
        label: isDRFADrillDown
          ? `DRFA: ${drfaCategoryName}`
          : isDRFABusinessDrillDown
            ? 'DRFA: Self-Employment Income (measured, one business account)'
            : isDRFACrossLedgerDrillDown
              ? 'DRFA: Self-Employment Income (measured, business activity in personal accounts)'
            : (STAT_LABELS[statKey] || statKey),
        onClear: () => navigate(`/report_sankey_analysis/${sankeyPerspective}`),
      }
    : null;

  return (
    <CaseDatabaseCheck>
      <CurrentCaseRequired>
        <TransactionAnalyzer
          transactions={transactions}
          isLoading={isLoading}
          mutate={mutate}
          perspective={sankeyPerspective}
          onPerspectiveChange={setSankeyPerspective}
          drilldown={drilldown}
          loadedCount={drilldownLoadedCount}
          totalCount={drilldownTotalCount}
          showWithheldFilter={!isDrillDown}
          onIncludeWithheldChange={setIncludeWithheld}
        />
      </CurrentCaseRequired>
    </CaseDatabaseCheck>
  );
}
